import { type RoleScope } from '../api/models/user.model';
import { useAuth } from '../providers/AuthProvider';
import { Navigate, useLocation } from 'react-router-dom';
import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ROUTE_ENROLLMENT, ROUTE_DISCLOSURE_START, ROUTE_INACTIVE, ROUTE_LOGIN } from './router';
import { useGetCurrentUserQuery } from '../api/user.api';
import { useUserHasRoleScope } from '../hooks/useUserType.ts';
import { useUserAccountRole } from '../hooks/useUserAccountRole';

export interface ProtectedRouteProps {
	allowedRoleScope?: RoleScope;
	restrictIfInvoicesBlocked?: boolean;
	children: React.ReactNode;
}

function ProtectedRoute({
	children,
	allowedRoleScope,
	restrictIfInvoicesBlocked,
}: ProtectedRouteProps) {
	const { isAuthenticated, loading, error } = useAuth();
	const location = useLocation();
	const userHasRoleScope = useUserHasRoleScope(allowedRoleScope);
	const { data: user, isLoading: userLoading } = useGetCurrentUserQuery();
	const { role } = useUserAccountRole();

	if (loading || userLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
				<CircularProgress size={50} />
			</Box>
		);
	}

	if (!loading && (!isAuthenticated || error)) {
		return <Navigate to={ROUTE_LOGIN} state={{ from: location }} replace />;
	}

	// If API_USER always redirect out of app (should not happen)
	if (role?.roleType === "API_USER") {
		return <Navigate to={ROUTE_LOGIN} replace />;
	}

	// Check if user has any roles at all
	// const userHasAnyRoles = !!user && !!user.roles && user.roles.length > 0;

	// TESTING: Redirect to disclosure page if user HAS accepted terms (ignoring roles check)
	if (isAuthenticated && !user?.acceptedTermsAndConditions) {
		return <Navigate to={`${ROUTE_ENROLLMENT}/${ROUTE_DISCLOSURE_START}`} />;
	}

	if (isAuthenticated && user?.userStatus && user.userStatus === 'INACTIVE') {
		return <Navigate to={`${ROUTE_LOGIN}/${ROUTE_INACTIVE}`} replace />;
	}

	// RBAC: If allowedPermissions is set and not empty, check if user has the correct role for the selected account
	if (allowedRoleScope) {
		if (!userHasRoleScope) {
			return <Navigate to="/" replace />;
		}
		/* Commenting out because we don't have any routes currently that require account-specific permissions, but leaving this here for future reference when we do
        if (selectedAccount && selectedAccount.accountId) {
            // Find the user's role for the selected account
            const accountRole = user?.roles?.find(r => r.accountId === selectedAccount.accountId);
            if (!accountRole) {
                // No role for this account, deny access
                return <Navigate to="/" replace />;
            }
            const hasPermission = allowedPermissions.some(allowed => String(accountRole.permissionLevel).toLowerCase() === String(allowed).toLowerCase());
            if (!hasPermission) {
                return <Navigate to="/" replace />;
            }
        } else if (selectedAccount && !selectedAccount.accountId) {
            // selectedAccount exists but has no accountId (invalid), deny access
            return <Navigate to="/" replace />;
        } else {
            // No account selected: fallback to global permissions check (legacy behavior)
            const userPermissions = user?.roles?.map(r => r.permissionLevel) || [];
            const hasPermission = userPermissions.some(perm =>
                allowedPermissions.some(allowed => String(perm).toLowerCase() === String(allowed).toLowerCase())
            );
            if (!hasPermission) {
                return <Navigate to="/" replace />;
            }
        }*/
	}

	// Check invoice restriction
	if (restrictIfInvoicesBlocked && role?.restrictInvoices) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
}

export default ProtectedRoute;
